import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { Error, useError } from "../../../common/components/AppState/Error";
import { setError } from "../../../common/state/slice/appStateSlice";
import { IsLoading } from "../../../common/components/AppState/IsLoading";
import { InputIcon, Input } from "../../../common/components/Input";
import { ButtonText } from "../../../common/components/Button";
import {
  confirmMfaEnrollment,
  getMfaEnrollment,
  sendMfaStepUpCode,
  startMfaEnrollment,
} from "../state/mfaSlice";
import type { IMfaEnrollConfirmResponse, MfaMethod } from "../state/mfaSlice";

export const MFA_ENROLL_START_COMPONENT_ID = "mfaEnrollStartComponent";
export const MFA_ENROLL_CONFIRM_COMPONENT_ID = "mfaEnrollConfirmComponent";
const MFA_ENROLL_STEP_UP_COMPONENT_ID = "mfaEnrollStepUpComponent";
const STEP_UP_COOLDOWN_SECONDS = 30;

type WizardStep = "chooseAndAuth" | "confirmIdentity" | "setup" | "confirm";

interface MfaEnrollDialogProps {
  isOpen: boolean;
  isReplacement: boolean;
  currentMethod?: "totp" | "email" | null;
  onClose: () => void;
  onEnrolled: (result?: IMfaEnrollConfirmResponse) => void;
  mfaToken?: string;
  variant?: "modal" | "inline";
}

const CODE_PATTERN = /^\d{6}$/;
const MFA_CHALLENGE_INVALID = "mfa_challenge_invalid";
const MFA_INVALID_CODE = "mfa_invalid_code";
const MFA_STEP_UP_REQUIRED = "mfa_step_up_required";
const ENROLLMENT_TTL_MS = 5 * 60 * 1000;

const formatCountdown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${rest.toString().padStart(2, "0")}`;
};

const MfaEnrollDialog = ({
  isOpen,
  isReplacement,
  currentMethod = null,
  onClose,
  onEnrolled,
  mfaToken,
  variant = "modal",
}: MfaEnrollDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const enrollment = useAppSelector(getMfaEnrollment);

  const [step, setStep] = useState<WizardStep>("chooseAndAuth");
  const [method, setMethod] = useState<MfaMethod>("totp");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [currentCodeOrRecovery, setCurrentCodeOrRecovery] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);
  const autoSubmittedCodeRef = useRef<string | null>(null);
  const startError = useError(MFA_ENROLL_START_COMPONENT_ID);
  const confirmError = useError(MFA_ENROLL_CONFIRM_COMPONENT_ID);
  const [enrollmentExpiresAt, setEnrollmentExpiresAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const isChallengeExpired =
    confirmError === MFA_CHALLENGE_INVALID || (enrollmentExpiresAt !== null && secondsLeft <= 0);

  const [stepUpToken, setStepUpToken] = useState<string | null>(null);
  const [stepUpSent, setStepUpSent] = useState(false);
  const [stepUpSending, setStepUpSending] = useState(false);
  const [stepUpCooldown, setStepUpCooldown] = useState(0);

  useEffect(() => {
    if (isOpen) {
      dispatch(setError({ componentId: MFA_ENROLL_START_COMPONENT_ID, error: "" }));
      dispatch(setError({ componentId: MFA_ENROLL_CONFIRM_COMPONENT_ID, error: "" }));
      dispatch(setError({ componentId: MFA_ENROLL_STEP_UP_COMPONENT_ID, error: "" }));
      setStep("chooseAndAuth");
      setMethod(isReplacement && currentMethod ? currentMethod : "totp");
      setPassword("");
      setCurrentCodeOrRecovery("");
      setConfirmCode("");
      setIsConfirming(false);
      autoSubmittedCodeRef.current = null;
      setEnrollmentExpiresAt(null);
      setSecondsLeft(0);
      setStepUpToken(null);
      setStepUpSent(false);
      setStepUpSending(false);
      setStepUpCooldown(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (enrollmentExpiresAt === null) {
      return undefined;
    }
    const tick = () =>
      setSecondsLeft(Math.max(0, Math.ceil((enrollmentExpiresAt - Date.now()) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [enrollmentExpiresAt]);

  const mfaErrorText = (error: string | undefined): string | undefined => {
    if (error === MFA_CHALLENGE_INVALID) {
      return t("pages.profile.mfa.enroll.challengeExpired");
    }
    if (error === MFA_INVALID_CODE) {
      return t("pages.login.mfaChallenge.invalidCode");
    }
    if (error === MFA_STEP_UP_REQUIRED) {
      return t("pages.profile.mfa.enroll.stepUpRequired");
    }
    if (error) {
      return t("pages.profile.mfa.enroll.genericError");
    }
    return undefined;
  };

  useEffect(() => {
    if (stepUpCooldown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => setStepUpCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [stepUpCooldown]);

  const handleSendStepUp = () => {
    if (stepUpCooldown > 0 || stepUpSending) {
      return;
    }
    setStepUpSending(true);
    setStepUpSent(false);
    void dispatch(sendMfaStepUpCode({ componentId: MFA_ENROLL_STEP_UP_COMPONENT_ID }))
      .unwrap()
      .then((res) => {
        setStepUpToken(res.step_up_token);
        setStepUpSent(true);
        setStepUpSending(false);
        setStepUpCooldown(STEP_UP_COOLDOWN_SECONDS);
      })
      .catch(() => {
        setStepUpSending(false);
        setStepUpSent(false);
      });
  };

  const handleBackToChooseMethod = () => {
    dispatch(setError({ componentId: MFA_ENROLL_START_COMPONENT_ID, error: "" }));
    dispatch(setError({ componentId: MFA_ENROLL_STEP_UP_COMPONENT_ID, error: "" }));
    setPassword("");
    setCurrentCodeOrRecovery("");
    setStep("chooseAndAuth");
  };

  const handleStartEnrollment = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isReplacement && step === "chooseAndAuth") {
      dispatch(setError({ componentId: MFA_ENROLL_START_COMPONENT_ID, error: "" }));
      setStep("confirmIdentity");
      return;
    }

    const trimmedCurrent = currentCodeOrRecovery.trim();
    const isSixDigitCode = CODE_PATTERN.test(trimmedCurrent);
    if (isReplacement && currentMethod === "email" && isSixDigitCode && !stepUpToken) {
      dispatch(
        setError({ componentId: MFA_ENROLL_START_COMPONENT_ID, error: MFA_STEP_UP_REQUIRED }),
      );
      return;
    }
    const canUseStepUp = currentMethod === "email" && Boolean(stepUpToken) && isSixDigitCode;
    const recoveryCodeOnly = currentMethod === "email" && !canUseStepUp;

    void dispatch(
      startMfaEnrollment({
        componentId: MFA_ENROLL_START_COMPONENT_ID,
        method,
        ...(mfaToken ? { mfaToken } : { password }),
        ...(isReplacement && trimmedCurrent
          ? recoveryCodeOnly || !isSixDigitCode
            ? { recoveryCode: trimmedCurrent }
            : canUseStepUp
            ? { currentOtp: trimmedCurrent, stepUpToken: stepUpToken as string }
            : { currentOtp: trimmedCurrent }
          : {}),
      }),
    ).then((res) => {
      if (startMfaEnrollment.fulfilled.match(res)) {
        setEnrollmentExpiresAt(Date.now() + ENROLLMENT_TTL_MS);
        setStep("setup");
      }
    });
  };

  const submitConfirmCode = useCallback(() => {
    const otp = confirmCode.trim();
    if (!enrollment?.mfaToken || isConfirming || !CODE_PATTERN.test(otp)) {
      return;
    }
    setIsConfirming(true);
    void dispatch(
      confirmMfaEnrollment({
        componentId: MFA_ENROLL_CONFIRM_COMPONENT_ID,
        mfaToken: enrollment.mfaToken,
        otp,
      }),
    ).then((res) => {
      setIsConfirming(false);
      if (confirmMfaEnrollment.fulfilled.match(res)) {
        onEnrolled(res.payload);
      } else {
        setConfirmCode("");
      }
    });
  }, [confirmCode, dispatch, enrollment?.mfaToken, isConfirming, onEnrolled]);

  useEffect(() => {
    if (step !== "confirm" || isConfirming || isChallengeExpired) {
      return;
    }
    if (!CODE_PATTERN.test(confirmCode)) {
      autoSubmittedCodeRef.current = null;
      return;
    }
    if (autoSubmittedCodeRef.current === confirmCode) {
      return;
    }
    autoSubmittedCodeRef.current = confirmCode;
    submitConfirmCode();
  }, [confirmCode, isChallengeExpired, isConfirming, step, submitConfirmCode]);

  const handleConfirmEnrollment = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    submitConfirmCode();
  };

  const handleStartAgain = () => {
    dispatch(setError({ componentId: MFA_ENROLL_CONFIRM_COMPONENT_ID, error: "" }));
    dispatch(setError({ componentId: MFA_ENROLL_START_COMPONENT_ID, error: "" }));
    setConfirmCode("");
    autoSubmittedCodeRef.current = null;
    setEnrollmentExpiresAt(null);
    setSecondsLeft(0);
    setStep("chooseAndAuth");
  };

  const countdownLine = (
    <p className="text-sm text-gray-500 mt-1" data-testid="mfa-enroll-countdown">
      {isChallengeExpired
        ? t("pages.profile.mfa.enroll.challengeExpired")
        : t("pages.profile.mfa.enroll.expiresIn", { time: formatCountdown(secondsLeft) })}
    </p>
  );

  const isInline = variant === "inline";

  const backToSignIn = isInline ? (
    <ButtonText type="button" text={t("pages.login.mfaChallenge.backToLogin")} onClick={onClose} />
  ) : null;

  const stepHeader = (title: string, subtitle: string) =>
    isInline ? (
      <div className="my-2">
        <h2 className="text-xl font-medium text-black">{title}</h2>
        <p className="text-sm">{subtitle}</p>
      </div>
    ) : (
      <>
        <h2 className="pt-3 pb-1 font-medium text-lg">{title}</h2>
        <h3 className="pb-3">{subtitle}</h3>
      </>
    );

  const loaders = (
    <>
      <IsLoading componentId={MFA_ENROLL_START_COMPONENT_ID} showSuccess={false} />
      <IsLoading componentId={MFA_ENROLL_CONFIRM_COMPONENT_ID} showSuccess={false} />
      <IsLoading componentId={MFA_ENROLL_STEP_UP_COMPONENT_ID} showSuccess={false} />
    </>
  );

  const content = (
    <div className="flex w-full">
      {!isInline && <div className="completely-centered z-50">{loaders}</div>}
      <div className={isInline ? "w-full" : "relative grow w-full max-w-7xl px-4 xl:px-0 mx-auto"}>
        {isInline && loaders}
        {step === "chooseAndAuth" && (
          <form onSubmit={handleStartEnrollment}>
            {stepHeader(
              isReplacement
                ? t("pages.profile.mfa.enroll.replaceTitle")
                : t("pages.profile.mfa.enroll.title"),
              isReplacement
                ? t("pages.profile.mfa.enroll.replaceChooseSubtitle")
                : mfaToken
                ? t("pages.login.mfaEnrollmentRequired.subtitle")
                : t("pages.profile.mfa.enroll.chooseMethodSubtitle"),
            )}

            <div className="border border-gray-200 rounded-lg overflow-hidden mt-1">
              {(["totp", "email"] as MfaMethod[]).map((option) => (
                <label
                  key={option}
                  className={`flex items-start gap-3 px-3 py-2 border-b border-gray-100 last:border-b-0 text-sm hover:bg-gray-50 cursor-pointer ${
                    method === option ? "bg-green-50" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="mfaEnrollMethod"
                    checked={method === option}
                    onChange={() => setMethod(option)}
                    className="mt-0.5"
                  />
                  <span className="flex-1">
                    <span className="font-medium text-gray-900">
                      {t(
                        option === "totp"
                          ? "pages.profile.mfa.enroll.methodTotp"
                          : "pages.profile.mfa.enroll.methodEmail",
                      )}
                    </span>
                    {option === "totp" && (
                      <span className="ml-2 badge-sucess text-[10px] align-middle">
                        {t("pages.profile.mfa.enroll.badgeRecommended")}
                      </span>
                    )}
                    {isReplacement && currentMethod === option && (
                      <span className="ml-2 text-xs text-gray-500">
                        {t("pages.profile.mfa.enroll.badgeCurrent")}
                      </span>
                    )}
                    <span className="block text-xs text-gray-500 mt-0.5">
                      {t(
                        option === "totp"
                          ? "pages.profile.mfa.enroll.methodTotpHint"
                          : "pages.profile.mfa.enroll.methodEmailHint",
                      )}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            {!mfaToken && !isReplacement && (
              <div className="mt-4">
                <InputIcon
                  autoFocus
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(event.target.value)
                  }
                  type={passwordVisible ? "text" : "password"}
                  position="right"
                  placeholder={t("pages.profile.mfa.passwordPlaceholder")}
                  handleClick={() => setPasswordVisible(!passwordVisible)}
                  icon={passwordVisible ? "EyeIcon" : "EyeOffIcon"}
                />
              </div>
            )}

            <Error
              componentId={MFA_ENROLL_START_COMPONENT_ID}
              errorString={mfaErrorText(startError)}
            />

            <div
              className={`flex items-center mt-4 ${
                isReplacement ? "justify-end space-x-3" : "justify-between"
              }`}
            >
              {backToSignIn}
              {isReplacement && (
                <button type="button" className="btn-alternative-outline" onClick={onClose}>
                  {t("general.cancel")}
                </button>
              )}
              <button
                className={`btn-primary-fill ${isReplacement ? "" : "ml-auto"}`}
                type="submit"
                disabled={!mfaToken && !isReplacement && !password}
              >
                {t("general.next")}
              </button>
            </div>
          </form>
        )}

        {step === "confirmIdentity" && (
          <form onSubmit={handleStartEnrollment}>
            {stepHeader(
              t("pages.profile.mfa.enroll.confirmIdentityTitle"),
              t("pages.profile.mfa.confirmSubtitle"),
            )}

            <div className="bg-red-100 rounded-lg p-3 text-red-700 font-medium text-sm mb-3">
              {t("pages.profile.mfa.confirmRegenerateWarning")}
            </div>

            <div className="mt-4">
              <InputIcon
                autoFocus
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setPassword(event.target.value)
                }
                type={passwordVisible ? "text" : "password"}
                position="right"
                placeholder={t("pages.profile.mfa.passwordPlaceholder")}
                handleClick={() => setPasswordVisible(!passwordVisible)}
                icon={passwordVisible ? "EyeIcon" : "EyeOffIcon"}
              />
            </div>

            <div className="mt-2">
              <Input
                type="text"
                value={currentCodeOrRecovery}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCurrentCodeOrRecovery(e.target.value)
                }
                className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 border-0"
                placeholder={
                  currentMethod === "email" && !stepUpToken
                    ? t("pages.profile.mfa.enroll.recoveryCodeOnlyPlaceholder")
                    : t("pages.profile.mfa.enroll.currentCodePlaceholder")
                }
              />
              {currentMethod === "email" && (
                <div className="flex items-center mt-1">
                  <ButtonText
                    type="button"
                    text={
                      stepUpCooldown > 0
                        ? t("pages.profile.mfa.enroll.stepUpCooldown", {
                            seconds: stepUpCooldown,
                          })
                        : t("pages.profile.mfa.enroll.emailStepUpButton")
                    }
                    onClick={handleSendStepUp}
                    disabled={stepUpCooldown > 0 || stepUpSending}
                  />
                </div>
              )}
              <p
                className={`text-xs text-green-600 mt-1 ${
                  stepUpSent && currentMethod === "email" ? "" : "invisible"
                }`}
              >
                {t("pages.profile.mfa.enroll.emailStepUpSent")}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {currentMethod === "email"
                  ? t("pages.profile.mfa.enroll.replaceHintEmail")
                  : t("pages.profile.mfa.enroll.replaceHint")}
              </p>
              <Error
                componentId={MFA_ENROLL_STEP_UP_COMPONENT_ID}
                errorString={t("pages.profile.mfa.enroll.stepUpSendFailed")}
              />
            </div>

            <Error
              componentId={MFA_ENROLL_START_COMPONENT_ID}
              errorString={mfaErrorText(startError)}
            />

            <div className="flex justify-end items-center mt-4 space-x-3">
              <button
                type="button"
                className="btn-alternative-outline"
                onClick={handleBackToChooseMethod}
              >
                {t("general.back")}
              </button>
              <button
                className="btn-primary-fill"
                type="submit"
                disabled={!password || !currentCodeOrRecovery}
              >
                {t("general.next")}
              </button>
            </div>
          </form>
        )}

        {step === "setup" && (
          <div>
            {stepHeader(
              t("pages.profile.mfa.enroll.setupTitle"),
              enrollment?.method === "totp"
                ? t("pages.profile.mfa.enroll.setupTotpInstructions")
                : t("pages.profile.mfa.enroll.setupEmailInstructions"),
            )}

            {countdownLine}

            {enrollment?.method === "totp" && (
              <>
                {enrollment.qrDataUri && (
                  <img
                    src={enrollment.qrDataUri}
                    alt={t("pages.profile.mfa.enroll.qrAlt")}
                    className="w-48 h-48 mx-auto"
                  />
                )}
                {enrollment.secret && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-500">
                      {t("pages.profile.mfa.enroll.manualEntryHint")}
                    </p>
                    <p className="select-all font-mono bg-white rounded-lg border border-gray-300 p-2 mt-1 break-all">
                      {enrollment.secret}
                    </p>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-between items-center mt-3">
              {backToSignIn}
              {isChallengeExpired ? (
                <button
                  className="btn-primary-fill ml-auto"
                  type="button"
                  onClick={handleStartAgain}
                >
                  {t("pages.profile.mfa.enroll.startAgain")}
                </button>
              ) : (
                <button
                  className="btn-primary-fill ml-auto"
                  type="button"
                  onClick={() => setStep("confirm")}
                >
                  {t("general.next")}
                </button>
              )}
            </div>
          </div>
        )}

        {step === "confirm" && (
          <form onSubmit={handleConfirmEnrollment}>
            {stepHeader(
              t("pages.profile.mfa.enroll.confirmTitle"),
              t("pages.profile.mfa.enroll.confirmSubtitle"),
            )}

            {countdownLine}

            <Input
              autoFocus
              type="text"
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              value={confirmCode}
              readOnly={isChallengeExpired}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setConfirmCode(e.target.value.replace(/\D/g, "").slice(0, 6))
              }
              onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                e.preventDefault();
                setConfirmCode(e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6));
              }}
              className="font-medium bg-white read-only:bg-slate-100 read-only:border-transparent read-only:shadow-none read-only:focus:ring-0 text-gray-600 border-0 tracking-[0.3em] text-center"
              placeholder={t("pages.profile.mfa.enroll.confirmPlaceholder")}
              data-testid="mfa-enroll-code"
            />

            {!isChallengeExpired && (
              <Error
                componentId={MFA_ENROLL_CONFIRM_COMPONENT_ID}
                errorString={mfaErrorText(confirmError)}
              />
            )}

            <div className="flex justify-between items-center mt-3">
              {backToSignIn}
              {isChallengeExpired ? (
                <button
                  className="btn-primary-fill ml-auto"
                  type="button"
                  onClick={handleStartAgain}
                >
                  {t("pages.profile.mfa.enroll.startAgain")}
                </button>
              ) : (
                <button
                  className="btn-primary-fill ml-auto"
                  type="submit"
                  disabled={isConfirming || !CODE_PATTERN.test(confirmCode.trim())}
                >
                  {t("general.confirm")}
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );

  if (isInline) {
    return isOpen ? content : null;
  }

  return (
    <ModalStandard
      id="modalMfaEnroll"
      fullScreen={false}
      controlledMode={{ isOpen }}
      showCloseButton
      onClose={onClose}
    >
      {content}
    </ModalStandard>
  );
};

export default MfaEnrollDialog;
